import { type ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { QueueTransactionCommandEnum } from "jadro-pico/enums/common/QueueTransactionCommandEnum";
import { useWebSocket } from "../WebSocket/useWebSocket";
import {
  OperationStatusEnum,
  operationRemovedAction,
  operationResolvedAction,
  operationStartedAction,
} from "../Redux/AppOperationsReducer";
import type { AppAuthState } from "../Redux/AppAuthReducer";
import { showSnackbar } from "../Components/UI/Snackbar/showSnackbar";
import {
  listSentUnresolvedMutations,
  markFailed,
  removeMutation,
} from "../Offline/pendingMutationsDb";
import { OPERATION_QUERY_KEY } from "../Api/queryKeys";
import { MAX_RECONCILED_OPERATIONS } from "jadro-pico/constants/operations";
import { OperationOutcomeEnum } from "jadro-pico/enums/common/OperationOutcomeEnum";
import type { ResolvedOperationDto } from "jadro-pico/dtos/pico_com/ResolvedOperationDto";
import { useResource } from "../Resources/useResource";
import { ProfileMenuResources } from "../Resources/ProfileMenuResources";
import { OperationsContext, type OperationOutcome } from "./OperationsContext";

const AUTO_RESOLVE_MS = 15000;
const COMPLETED_LINGER_MS = 4000;

interface OperationsProviderProps {
  wsUrl: string;
  clientId: string;
  children: ReactNode;
}

export function OperationsProvider({ wsUrl, clientId, children }: OperationsProviderProps) {
  const dispatch = useDispatch();
  const t = useResource(ProfileMenuResources);
  const user = useSelector((s: { auth: AppAuthState }) => s.auth.user);

  const queryClient = useQueryClient();
  const waitersRef = useRef(new Map<string, (outcome: OperationOutcome) => void>());
  const autoTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const clearAutoTimer = useCallback((operationUuid: string) => {
    const timer = autoTimersRef.current.get(operationUuid);
    if (timer) {
      clearTimeout(timer);
      autoTimersRef.current.delete(operationUuid);
    }
  }, []);

  const resolveStatus = useCallback(
    (operationUuid: string, status: OperationStatusEnum.COMPLETED | OperationStatusEnum.FAILED) => {
      clearAutoTimer(operationUuid);
      void queryClient.invalidateQueries({ queryKey: OPERATION_QUERY_KEY });
      dispatch(operationResolvedAction(operationUuid, status));
      if (status === OperationStatusEnum.COMPLETED) {
        setTimeout(() => dispatch(operationRemovedAction(operationUuid)), COMPLETED_LINGER_MS);
      }
    },
    [clearAutoTimer, dispatch, queryClient],
  );

  const { connected, send } = useWebSocket({
    url: wsUrl,
    clientId,
    enabled: !!user,
    onMessage: (msg) => {
      if (msg.type === QueueTransactionCommandEnum.OPERATIONS_RESOLVED) {
        void applyResolvedOutcomes(msg.data as { resolved?: ResolvedOperationDto[] } | undefined);
        return;
      }
      const operationUuid = (msg.data as { operationUuid?: string } | undefined)?.operationUuid;
      if (!operationUuid) return;
      const waiter = waitersRef.current.get(operationUuid);
      if (msg.type === QueueTransactionCommandEnum.OPERATION_COMPLETED) {
        resolveStatus(operationUuid, OperationStatusEnum.COMPLETED);
        // The outbox entry exists until the write is confirmed; this is that confirmation.
        void removeMutation(operationUuid);
        waiter?.("completed");
        waitersRef.current.delete(operationUuid);
      } else if (msg.type === QueueTransactionCommandEnum.OPERATION_FAILED) {
        resolveStatus(operationUuid, OperationStatusEnum.FAILED);
        void markFailed(operationUuid, 0);
        showSnackbar(dispatch, t.operationFailedToast);
        waiter?.("failed");
        waitersRef.current.delete(operationUuid);
      }
    },
  });

  // Every outstanding entry is asked about up front, in batches. Asking again only after a batch
  // resolved something would stall forever on a batch where nothing had been written yet.
  const requestOutstandingOutcomes = useCallback(async (): Promise<void> => {
    if (!user) return;
    const outstanding = await listSentUnresolvedMutations(user.sub);
    for (let from = 0; from < outstanding.length; from += MAX_RECONCILED_OPERATIONS) {
      send({
        type: QueueTransactionCommandEnum.RESOLVE_OPERATIONS,
        operationUuids: outstanding
          .slice(from, from + MAX_RECONCILED_OPERATIONS)
          .map((entry) => entry.operationUuid),
      });
    }
  }, [send, user]);

  const applyResolvedOutcomes = useCallback(
    async (data: { resolved?: ResolvedOperationDto[] } | undefined): Promise<void> => {
      const resolved = data?.resolved ?? [];
      if (resolved.length === 0) return;
      for (const row of resolved) {
        if (row.outcome === OperationOutcomeEnum.PROCESSED) await removeMutation(row.operationUuid);
        else await markFailed(row.operationUuid, 0);
      }
      // The page decides "could not be sent" from "not in the history", so a stale history would
      // show the wrong wording for exactly the rows just reconciled.
      void queryClient.invalidateQueries({ queryKey: OPERATION_QUERY_KEY });
    },
    [queryClient],
  );

  useEffect(() => {
    if (connected) void requestOutstandingOutcomes();
  }, [connected, requestOutstandingOutcomes]);

  const failOperation = useCallback(
    (operationUuid: string) => resolveStatus(operationUuid, OperationStatusEnum.FAILED),
    [resolveStatus],
  );

  const trackOperation = useCallback(
    (operationUuid: string, label: string) => {
      dispatch(operationStartedAction(operationUuid, label, Date.now()));
      const timer = setTimeout(() => {
        autoTimersRef.current.delete(operationUuid);
        dispatch(operationRemovedAction(operationUuid));
      }, AUTO_RESOLVE_MS);
      autoTimersRef.current.set(operationUuid, timer);
    },
    [dispatch],
  );

  const waitForOperation = useCallback(
    (operationUuid: string, timeoutMs: number): Promise<OperationOutcome> => {
      // Resolving straight away when the socket is still connecting would report every save made
      // during a page load as confirmed; the timeout is the only honest bound.
      return new Promise<OperationOutcome>((resolve) => {
        const timer = setTimeout(() => {
          waitersRef.current.delete(operationUuid);
          resolve("timeout");
        }, timeoutMs);
        waitersRef.current.set(operationUuid, (outcome) => {
          clearTimeout(timer);
          resolve(outcome);
        });
      });
    },
    [],
  );

  const value = useMemo(
    () => ({ trackOperation, failOperation, waitForOperation }),
    [trackOperation, failOperation, waitForOperation],
  );

  return <OperationsContext.Provider value={value}>{children}</OperationsContext.Provider>;
}
